import React, { useState, useEffect, useMemo, useContext } from 'react'
import styled, { css, keyframes } from 'styled-components'
import { useTranslation } from 'react-i18next'
import { DragDropContext, Droppable } from 'react-beautiful-dnd'
import { useSelector, useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { propOr } from 'ramda'
import * as R from 'ramda'
import { showToastRoutine } from 'modules/toast/ducks/actions'
import { SEVERITY } from 'utils/toast'
import { getErrorMessage } from 'utils/errors'

import saltyCalendarInit from 'assets/images/saltyCalendarInit.svg'

import {
  Button,
  SingleSelect,
  BouncingLoader,
  CalendarIcon,
  Tooltip
} from 'examkrackers-components'

import { DATE_FORMATS, noTimezoneDate, removeUTCFromDate } from 'utils/date'
import {
  ANALYTICS_EVENTS,
  handleActionAnalytics,
  getCourseTypeForAnalyticsEvents
} from 'utils/analytics'
import {
  columnsForExpiration,
  columnsOrderForExpiration,
  daysOfWeek,
  MIN_DAYS_FOR_PRE_READING_SETUP,
  DAYS_BEFORE_FIRST_CLASS_FOR_PRE_READING_END_DATE
} from 'utils/calendar'
import { getOptionByValue } from 'utils/form'
import { isNilOrEmpty, isNotNilOrEmpty } from 'utils/ramda'
import PATHS from 'utils/paths'

import {
  getMcatDates,
  setExamDate,
  setPrioriDays,
  postBuildPreReading
} from 'services/CalendarService'
import AnalyticsService from 'services/AnalyticsService'

import { getStudent } from 'modules/auth/ducks/selectors'

import DateInput from 'components/DateInput/DateInput'
import LeftColumnQuestions from './LeftColumnQuestions'
import DayListItem from './DayListItem'
import useCalendarSettingsValidationForExpiration from 'hooks/useCalendarSettingsValidationForExpiration'

import {
  subDays,
  isAfter,
  parse,
  isEqual,
  differenceInDays,
  addDays,
  format
} from 'date-fns'
import PreReadingModal from 'modules/calendar/PreReadingModal'
import withSettingsPageWrapper from 'modules/calendar/SettingsPageWrapper'
import { CalendarContext, CalendarContextProvider } from 'hooks/CalendarContext'
import { Course } from 'types'

interface McatDate {
  id: string
  course_id: string
  mcat_date: string
}

const initialValues = {
  mcat_at: '',
  calendar_start_at: '',
  exam_at: '',
  prioriDays: [0, 0, 0, 0, 0, 0, 0],
  prereading_end_date: ''
}

const WrappedCalendarInitForExpiration = () => {
  return (
    <CalendarContextProvider>
      <CalendarInitForExpiration />
    </CalendarContextProvider>
  )
}

const CalendarInitForExpiration: React.FC = () => {
  const [values, setValues] = useState(initialValues)
  const [open, setOpen] = useState(true)
  const [datesMcat, setDatesMcat] = useState<McatDate[]>([])
  const [hoveringDate, setHoveringDate] = useState<string | null>(null)

  const [columnsData, setColumnsData] = useState(columnsForExpiration)
  const [isLoading, setIsLoading] = React.useState<boolean>(false)
  const [withoutPreReading, setWithoutPreReading] = useState<boolean>(false)

  const { course } = useContext(CalendarContext)
  const courseFromContext = course as Course

  const { t } = useTranslation()

  const { push } = useHistory()
  const dispatch = useDispatch()

  const showToast = React.useCallback(
    (payload: { key: string; severity: string; options?: any }) => dispatch(showToastRoutine(payload)),
    [dispatch]
  )

  const isLiveCourse = course?.type === 'live_course'

  const calendarStartAt = values.calendar_start_at

  const firstClassDate: string | null = R.pipe(
    R.pathOr('', ['endDateFirstDay', 'class_date']),
    removeUTCFromDate
  )(course)

  const courseAccessibleTo: string =
    R.pipe(R.propOr('', 'accessible_to'), removeUTCFromDate)(course) ?? ''

  const firstClassDateValid =
    firstClassDate != null &&
    typeof firstClassDate === 'string' &&
    firstClassDate.length >= 10

  const firstClassDateAsDate =
    firstClassDateValid && firstClassDate
      ? noTimezoneDate(firstClassDate)
      : null

  const dayBeforeFirstClassDate =
    firstClassDateAsDate != null
      ? subDays(firstClassDateAsDate, 1)
      : null

  const minPreReadingEndDate = addDays(calendarStartAt, 1)

  const maxPreReadingEndDate =
    firstClassDateAsDate != null
      ? subDays(
          firstClassDateAsDate,
          DAYS_BEFORE_FIRST_CLASS_FOR_PRE_READING_END_DATE
        )
      : null

  const parsedFirstClassDate = firstClassDateValid
    ? parse(firstClassDate.slice(0, 10), 'yyyy-MM-dd', new Date())
    : null
  const parsedHoveringDate = hoveringDate
    ? parse(hoveringDate as string, 'MMMM dd, yyyy', new Date())
    : ''
  const hoverDate = hoveringDate ? noTimezoneDate(hoveringDate) : null
  const displayAfterFirstClassHelperText =
    hoverDate != null &&
    firstClassDateAsDate != null &&
    (isAfter(hoverDate, firstClassDateAsDate) ||
      (parsedFirstClassDate != null &&
        isEqual(parsedFirstClassDate, parsedHoveringDate)))

  const calendarStartDate = noTimezoneDate(calendarStartAt)
  const daysBetweenStartDateAndFirstClass =
    firstClassDateAsDate != null && calendarStartDate != null
      ? Math.abs(
          differenceInDays(calendarStartDate, firstClassDateAsDate)
        )
      : 0

  const canCalendarHaveSpaceForPreReading =
    isLiveCourse &&
    daysBetweenStartDateAndFirstClass >= MIN_DAYS_FOR_PRE_READING_SETUP

  const accessibleTo =
    R.pipe(R.propOr('', 'accessible_to'), removeUTCFromDate)(course) ?? ''

  const courseId = course.book_course_id
  const courseStudentId = course.id

  const user = useSelector(getStudent)

  const mcatDateOptions: { label: string; value: string }[] = useMemo(() => {
    return datesMcat
      .filter(date => {
        const d = noTimezoneDate(removeUTCFromDate(date.mcat_date))
        return d != null && isAfter(d, new Date())
      })
      .sort((a, b) => {
        const dA = noTimezoneDate(removeUTCFromDate(a.mcat_date))
        const dB = noTimezoneDate(removeUTCFromDate(b.mcat_date))
        return (dA?.getTime() ?? 0) - (dB?.getTime() ?? 0)
      })
      .map(date => {
        const d = noTimezoneDate(removeUTCFromDate(date.mcat_date))
        return {
          label: d != null ? format(d, DATE_FORMATS.slash) : '',
          value: propOr('', 'id', date) as string
        }
      })
      .filter((opt): opt is { label: string; value: string } => opt.label !== '')
  }, [datesMcat])

  const courseAnalysis = () => {
    const action = handleActionAnalytics(course as Course)
    let data = {}

    if (action === 'start') {
      data = {
        Time: format(new Date(), DATE_FORMATS.dashedWithTime),
        'Course type': course?.type || false,
        'Course name': course?.title || false,
        'Course expiration date': mcat_at || false,
        'Course class end date': `${course?.calendar_start_at}` || false
      }
    } else {
      data = {
        Time: format(new Date(), DATE_FORMATS.dashedWithTime),
        'Course type': course?.type || false,
        'Course name': course?.title || false,
        'Course expiration date': mcat_at || false
      }
    }

    AnalyticsService(user).logEvent(
      ANALYTICS_EVENTS[
        getCourseTypeForAnalyticsEvents(action, course?.type as string)
      ],
      data
    )
  }

  const redirectToCourseCalendar = prioriDays => {
    courseAnalysis()
    const isPrioriDays = isNotNilOrEmpty(prioriDays)

    isPrioriDays ? push(PATHS.calendar) : push(PATHS.calendarSetupExpiration)
  }

  const { dndErrors, errors, touchedFields, setTouchedFields } =
    useCalendarSettingsValidationForExpiration({
      course: courseFromContext,
      values,
      mcatDateOptions,
      columnsData,
      isReorder: false,
      canCalendarHaveSpaceForPreReading: !withoutPreReading
    })

  const handleDragEnd = result => {
    const { source, destination, draggableId } = result

    setTouchedFields(prev => [...prev, 'priori_days'])

    if (destination.droppableId === 'prioritySix') {
      values.prioriDays[daysOfWeek.indexOf(draggableId)] = 6
    } else if (destination.droppableId === 'prioritySeven') {
      values.prioriDays[daysOfWeek.indexOf(draggableId)] = 7
    } else if (destination.droppableId === 'priorityOne') {
      values.prioriDays[daysOfWeek.indexOf(draggableId)] = 1
    } else if (destination.droppableId === 'priorityTwo') {
      values.prioriDays[daysOfWeek.indexOf(draggableId)] = 2
    } else if (destination.droppableId === 'priorityThree') {
      values.prioriDays[daysOfWeek.indexOf(draggableId)] = 3
    } else if (destination.droppableId === 'priorityFour') {
      values.prioriDays[daysOfWeek.indexOf(draggableId)] = 4
    } else if (destination.droppableId === 'priorityFive') {
      values.prioriDays[daysOfWeek.indexOf(draggableId)] = 5
    } else {
      values.prioriDays[daysOfWeek.indexOf(draggableId)] = 0
    }

    if (!destination) {
      return
    }

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return
    }

    const startColumn = columnsData[source.droppableId]
    const finishColumn = columnsData[destination.droppableId]

    if (startColumn === finishColumn) {
      const newDay = Array.from(startColumn.day)
      const [removed] = newDay.splice(0, 1)
      newDay.splice(destination.index, 0, removed)

      const newColumn = {
        ...startColumn,
        day: newDay
      }

      const newState = {
        ...columnsData,
        [source.droppableId]: newColumn
      }
      setColumnsData(newState)
    } else {
      const startNewDay = startColumn.day
      const [removed] = startNewDay.splice(0, 1)
      const newStartColumn = {
        ...startColumn,
        day: startNewDay
      }
      const finishNewDay = Array.from(finishColumn.day)
      finishNewDay.splice(destination.index, 0, removed)
      const newFinishColumn = {
        ...finishColumn,
        day: finishNewDay
      }

      const newState = {
        ...columnsData,
        [source.droppableId]: newStartColumn,
        [destination.droppableId]: newFinishColumn
      }

      setColumnsData(newState)
    }
  }

  const handleChangeMcatDate = option => {
    setValues(prev => ({ ...prev, mcat_at: option.value }))
    setTouchedFields(prev => [...prev, 'mcat_at'])
  }

  const handleValueChange = (name, value) => {
    setValues(prev => ({ ...prev, [name]: value }))
    setTouchedFields(prev => [...prev, name])
    if (name === 'prereading_end_date' || name === 'calendar_start_at') {
      setValues(prev => ({ ...prev, exam_at: accessibleTo }))
    }
  }

  const handleSaveDate = async () => {
    setIsLoading(true)
    try {
      await setPrioriDays({
        id: courseStudentId as string,
        prioridays:
          JSON.stringify(values.prioriDays) ===
          JSON.stringify([0, 0, 0, 0, 0, 0, 0])
            ? [6, 0, 1, 4, 2, 3, 7]
            : values.prioriDays
      })
      await setExamDate({
        calendar_start_at: values.calendar_start_at,
        exam_at: accessibleTo,
        course_id: courseStudentId as string,
        mcat_date_id: values.mcat_at,
        build_calendar: -3,
        is_pre_reading: true,
        pre_reading_end_date: values.prereading_end_date
      })
      await postBuildPreReading({
        date_end: values.prereading_end_date,
        date_start: values.calendar_start_at
      })

      courseAnalysis()

      setIsLoading(false)
      await redirectToCourseCalendar(values.prioriDays)
    } catch (e) {
      setIsLoading(false)
      const errorMessage = getErrorMessage(e as Error) || 
        (e as any)?.response?.data?.message || 
        (e as any)?.message || 
        ''
      
      if (errorMessage) {
        showToast({
          key: 'toast.custom',
          severity: SEVERITY.error,
          options: {
            content: errorMessage
          }
        })
      } else {
        showToast({
          key: 'toast.somethingWentWrong',
          severity: SEVERITY.error
        })
      }
    }
  }

  const handleSaveDateWithoutPreReading = async () => {
    setIsLoading(true)

    try {
      await setPrioriDays({
        id: courseStudentId as string,
        prioridays: values.prioriDays
      })

      await setExamDate({
        calendar_start_at: values.calendar_start_at,
        exam_at: accessibleTo,
        course_id: courseStudentId as string,
        mcat_date_id: values.mcat_at,
        build_calendar: -3,
        is_pre_reading: false
      })

      courseAnalysis()

      setIsLoading(false)
    } catch (e) {
      setIsLoading(false)
      const errorMessage = getErrorMessage(e as Error) || 
        (e as any)?.response?.data?.message || 
        (e as any)?.message || 
        ''
      
      if (errorMessage) {
        showToast({
          key: 'toast.custom',
          severity: SEVERITY.error,
          options: {
            content: errorMessage
          }
        })
      } else {
        showToast({
          key: 'toast.somethingWentWrong',
          severity: SEVERITY.error
        })
      }
    } finally {
      push(PATHS.calendar)
    }
  }

  const handleSaveDateAndCheckIfPreReadingIsPossible =
    canCalendarHaveSpaceForPreReading && !withoutPreReading
      ? handleSaveDate
      : handleSaveDateWithoutPreReading

  useEffect(() => {
    courseId &&
      getMcatDates({ id: courseId })
        .then(resp => {
          const dates = resp?.data?.data ?? []
          if (dates?.length === 0) {
            return
          }
          setDatesMcat(dates)
        })
        .catch(err => {
          console.error(err)
        })
  }, [])

  const { calendar_start_at, mcat_at } = values

  const shouldShowPreReadingModal =
    isNotNilOrEmpty(mcat_at) &&
    isNotNilOrEmpty(calendar_start_at) &&
    canCalendarHaveSpaceForPreReading

  useEffect(() => {
    setOpen(shouldShowPreReadingModal)
  }, [shouldShowPreReadingModal])

  const handleCalendarManualOpen = (id: string) => () => {
    const el = document.getElementById(id)

    if (el) {
      el.click()
    }
  }

  const handleHoverOverPrereadingEndDateSetup = e => {
    const canSetHoveringDate = e.target.ariaLabel ? true : false

    const isDateNotAvailable = e.target.ariaLabel
      ? e.target.ariaLabel.includes('ot available')
      : null
    const hoveringDateStringContentsArray = e.target.ariaLabel
      ? e.target.ariaLabel.split(' ')
      : []

    const currentlyHoveringDate = isDateNotAvailable
      ? hoveringDateStringContentsArray
          .slice(3, hoveringDateStringContentsArray.length)
          .join(' ')
          .toString()
          .replace(/(\d+)(st|nd|rd|th)/gi, '$1')
      : hoveringDateStringContentsArray
          .slice(2, hoveringDateStringContentsArray.length)
          .join(' ')
          .toString()
          .replace(/(\d+)(st|nd|rd|th)/gi, '$1')

    canSetHoveringDate ? setHoveringDate(currentlyHoveringDate) : null
  }

  useEffect(() => {
    if (
      isNotNilOrEmpty(values.calendar_start_at) &&
      maxPreReadingEndDate != null
    ) {
      setValues(prev => ({
        ...prev,
        prereading_end_date: format(maxPreReadingEndDate, DATE_FORMATS.dashed)
      }))
    }
  }, [values.calendar_start_at])

  if (isLoading) {
    return (
      <LoaderContainer>
        <BouncingLoader />
      </LoaderContainer>
    )
  }

  return (
    <Container data-testid='CalendarInitForExpiration-Container'>
      <PreReadingModal
        data-testid='CalendarInitForExpiration-PreReadingModal'
        open={open}
        setOpen={setOpen}
        setWithoutPreReading={setWithoutPreReading}
      />
      <TitleWrapper data-testid='CalendarInitForExpiration-TitleWrapper'>
        {t('calendar.setup.title')}
      </TitleWrapper>
      <SubtitleWrapper data-testid='CalendarInitForExpiration-SubtitleWrapper'>
        {t('calendar.setup.description')}
      </SubtitleWrapper>
      <ColumnsWrapper>
        <LeftColumn data-testid='CalendarInitForExpiration-LeftColumn'>
          <QuestionContainer data-testid='CalendarInitForExpiration-QuestionContainer'>
            <DescWrapper>
              <QuestionTitle data-testid='CalendarInitForExpiration-QuestionTitle'>
                {t('calendar.setup.mcat.question')}
              </QuestionTitle>
              <QuestionDescription data-testid='CalendarInitForExpiration-QuestionDescription'>
                {t('calendar.setup.mcat.tooltip')}
              </QuestionDescription>
            </DescWrapper>
            <SelectWrapper data-testid='CalendarInitForExpiration-SelectWrapper'>
              <StyledSelect
                data-testid='CalendarInitForExpiration-StyledSelect'
                removeMargin
                label=''
                size='small'
                options={mcatDateOptions}
                onChange={handleChangeMcatDate}
                disabled={isNilOrEmpty(mcatDateOptions)}
                value={getOptionByValue(values.mcat_at)(mcatDateOptions)}
              />
            </SelectWrapper>
          </QuestionContainer>
          {errors.mcatDate && touchedFields.includes('mcat_at') && (
            <Error data-testid='CalendarInitForExpiration-Error-mcatDate'>
              {errors.mcatDate}
            </Error>
          )}
          <QuestionContainer
            data-testid='CalendarInitForExpiration-QuestionContainer'
            disabled={isNilOrEmpty(values.mcat_at)}
          >
            <DescWrapper>
              <QuestionTitle data-testid='CalendarInitForExpiration-QuestionTitle-start_at'>
                {t('calendar.setup.start.question')}
              </QuestionTitle>
              <QuestionDescription>
                {t('calendar.setup.start.tooltip')}
              </QuestionDescription>
            </DescWrapper>

            <PickerWrapper>
              <DateInput
                data-testid='CalendarInitForExpiration-DateInput-start_at'
                name='calendar_start_at'
                id='calendar_start_at_input'
                value={values.calendar_start_at}
                validate={() => true}
                size='small'
                onChange={handleValueChange}
                minDate={new Date()}
                maxDate={
                  dayBeforeFirstClassDate != null
                    ? isAfter(new Date(), dayBeforeFirstClassDate)
                      ? noTimezoneDate(courseAccessibleTo) ?? undefined
                      : dayBeforeFirstClassDate
                    : undefined
                }
              />
            </PickerWrapper>

            <IconContainer>
              <CalendarIcon
                data-testid='CalendarInitForExpiration-CalendarIcon-start_at'
                onClick={handleCalendarManualOpen('calendar_start_at_input')}
              />
            </IconContainer>
          </QuestionContainer>
          {errors.startDate && touchedFields.includes('calendar_start_at') && (
            <Error>{errors.startDate}</Error>
          )}
          {canCalendarHaveSpaceForPreReading && !withoutPreReading ? (
            <QuestionContainer
              data-testid='CalendarInitForExpiration-QuestionContainer-preReadingEnd'
              disabled={isNilOrEmpty(values.calendar_start_at) || open}
            >
              <DescWrapper>
                <QuestionTitle data-testid='CalendarInitForExpiration-QuestionTitle-preReadingEnd'>
                  {t('calendar.setupForExpiration.end.question')}
                </QuestionTitle>
                <QuestionDescription>
                  <QuestionContent data-testid='CalendarInitForExpiration-QuestionContent'>
                    {t('calendar.setupForExpiration.end.tooltip')}
                  </QuestionContent>
                </QuestionDescription>
              </DescWrapper>
              <PickerWrapper
                onMouseMove={handleHoverOverPrereadingEndDateSetup}
              >
                <DateInput
                  data-testid='CalendarInitForExpiration-DateInput-preReadingEnd'
                  name='prereading_end_date'
                  id='prereading_end_date_input'
                  value={values.prereading_end_date}
                  validate={() => true}
                  size='small'
                  // t={t}
                  onChange={handleValueChange}
                  minDate={minPreReadingEndDate}
                  maxDate={maxPreReadingEndDate ?? undefined}
                  course={course as Course}
                  withExtendModal
                />
              </PickerWrapper>
              <IconContainer>
                <CalendarIcon
                  data-testid='CalendarInitForExpiration-CalendarIcon-preReadingEnd'
                  onClick={handleCalendarManualOpen(
                    'prereading_end_date_input'
                  )}
                />
              </IconContainer>
            </QuestionContainer>
          ) : null}
          {errors.prereading_end_date &&
            canCalendarHaveSpaceForPreReading &&
            touchedFields.includes('prereading_end_date') && (
              <Error data-testid='CalendarInitForExpiration-Error-preReadingEnd'>
                {errors.prereading_end_date}
              </Error>
            )}
          {displayAfterFirstClassHelperText ? (
            <ErrorPrereading>
              {t('calendar.setupForExpiration.end.error')}
            </ErrorPrereading>
          ) : null}

          {canCalendarHaveSpaceForPreReading && !withoutPreReading && (
            <LeftColumnQuestions
              data-testid='CalendarInitForExpiration-LeftColumnQuestions'
              disabled={isNilOrEmpty(values.prereading_end_date) || open}
              isLiveCourse
            />
          )}
        </LeftColumn>
        {canCalendarHaveSpaceForPreReading &&
        !withoutPreReading &&
        isNotNilOrEmpty(values.prereading_end_date) ? (
          <RightColumn data-testid='CalendarInitForExpiration-RightColumn'>
            <ImageContainer data-testid='CalendarInitForExpiration-ImageContainer-saltyCalendarInit'>
              <img src={saltyCalendarInit} alt='book-cover-all' />
            </ImageContainer>
            <DragDropContext
              data-testid='CalendarInitForExpiration-DragDropContext'
              onDragEnd={handleDragEnd}
            >
              <WrapperDnd
                data-testid='CalendarInitForExpiration-WrapperDnd'
                disabled={isNilOrEmpty(values.exam_at)}
              >
                <WeekDaysColumn data-testid='CalendarInitForExpiration-WeekDaysColumn'>
                  <>
                    <ColumnTitle data-testid='CalendarInitForExpiration-ColumnTitle'>
                      {t('calendar.setup.mandatoryDaysOff')}
                    </ColumnTitle>
                    {columnsOrderForExpiration.map((columnId, index) => {
                      const column = columnsData[columnId]
                      if (index <= 6) {
                        return (
                          <DropZoneWrapper
                            key={column.id}
                            data-testid={`CalendarInitForExpiration-DropZoneWrapper-${column.id}`}
                          >
                            <Droppable
                              data-testid={`CalendarInitForExpiration-Droppable-WeekDays-${column.id}`}
                              droppableId={column.id}
                              isDropDisabled={
                                column.day && column.day.length === 1
                              }
                            >
                              {(provided, snapshot) => {
                                return (
                                  <SingleDropZone
                                    data-testid={`CalendarInitForExpiration-SingleDropZone-WeekDays-${column.id}`}
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                  >
                                    <DropContainer
                                      data-testid={`CalendarInitForExpiration-DropContainer-WeekDays-${column.title}`}
                                    >
                                      {column.title}
                                    </DropContainer>

                                    {isNotNilOrEmpty(column.day) && (
                                      <DayListItem
                                        data-testid={`CalendarInitForExpiration-DayListItem-WeekDays-${column.day[0]}`}
                                        day={column.day[0]}
                                        index={index}
                                      />
                                    )}
                                    {provided.placeholder}
                                  </SingleDropZone>
                                )
                              }}
                            </Droppable>
                          </DropZoneWrapper>
                        )
                      } else {
                        return null
                      }
                    })}
                  </>
                </WeekDaysColumn>

                <PrioriColumn>
                  <>
                    <ColumnTitle data-testid='CalendarInitForExpiration-ColumnTitle-studyDays'>
                      {t('calendar.setup.studyDays')}
                    </ColumnTitle>
                    {columnsOrderForExpiration.map((columnId, index) => {
                      const column = columnsData[columnId]
                      if (index > 6) {
                        return (
                          <DropZoneWrapper
                            data-testid={`CalendarInitForExpiration-DropZoneWrapper-studyDays-${column.id}`}
                            key={column.id}
                          >
                            <Droppable
                              data-testid={`CalendarInitForExpiration-Droppable-studyDays-${column.id}`}
                              droppableId={column.id}
                              isDropDisabled={
                                column.day && column.day.length === 1
                              }
                            >
                              {(provided, snapshot) => {
                                return (
                                  <SingleDropZoneActive
                                    data-testid={`CalendarInitForExpiration-SingleDropZoneActive-studyDays-${column.id}`}
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                    isDraggingOver={snapshot.isDraggingOver}
                                  >
                                    <DropContainer
                                      data-testid={`CalendarInitForExpiration-DropContainer-studyDays-${column.title}`}
                                    >
                                      {column.title}
                                    </DropContainer>
                                    {isNotNilOrEmpty(column.day) && (
                                      <DayListItem
                                        data-testid={`CalendarInitForExpiration-DayListItem-studyDays-${column.day[0]}`}
                                        day={column.day[0]}
                                        index={index}
                                      />
                                    )}
                                    {provided.placeholder}
                                  </SingleDropZoneActive>
                                )
                              }}
                            </Droppable>
                          </DropZoneWrapper>
                        )
                      } else {
                        return null
                      }
                    })}
                  </>
                </PrioriColumn>
              </WrapperDnd>
            </DragDropContext>
            {isNotNilOrEmpty(dndErrors) &&
              touchedFields.includes('priori_days') && (
                <DndErrors>
                  {dndErrors.map(error => (
                    <Error>{error}</Error>
                  ))}
                </DndErrors>
              )}
          </RightColumn>
        ) : null}
      </ColumnsWrapper>
      <ButtonWrapper data-testid='CalendarInitForExpiration-ButtonWrapper'>
        {isNotNilOrEmpty(errors) ? (
          <Tooltip
            id='inactive-button'
            tooltipContent='This form must be completed in order to continue. You will be able to make changes at any time by clicking on the Reorganize button on your calendar.'
          >
            <Button
              data-testid='CalendarInitForExpiration-Button-setup-disabled'
              variant='contained'
              color='secondary'
              size='small'
              onClick={handleSaveDateAndCheckIfPreReadingIsPossible}
              disabled
            >
              {t('calendar.setup.button')}
            </Button>
          </Tooltip>
        ) : (
          <Button
            data-testid='CalendarInitForExpiration-Button-setup'
            variant='contained'
            color='secondary'
            size='small'
            onClick={handleSaveDateAndCheckIfPreReadingIsPossible}
            disabled={isNotNilOrEmpty(errors)}
          >
            {t('calendar.setup.button')}
          </Button>
        )}
      </ButtonWrapper>
    </Container>
  )
}

export default withSettingsPageWrapper(WrappedCalendarInitForExpiration)

const Container = styled.div`
  margin: 20px 132px;
  font-size: 12px;
  padding-bottom: 30px;
`

const TitleWrapper = styled.div`
  text-align: center;
  font-size: 22px;
  font-weight: 700;
  line-height: 28px;
`

const SubtitleWrapper = styled.div`
  text-align: center;
  font-size: 12px;
  line-height: px;
  margin-top: 10px;
`

const LeftColumn = styled.div`
  width: 50%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`

const fadeId = keyframes`
from {
    opacity: 0;
    transform: translateY(20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const RightColumn = styled.div`
  width: 50%;
  display: flex;
  flex-direction: column;
  animation: ${fadeId} 200ms ease-in-out;
`

const ColumnsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`

const ImageContainer = styled.div`
  margin-top: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 65px;
`
const SingleDropZone = styled.div`
  color: ${({ theme }) => theme.colors.main.grey400};
  background-color: #fbf5f2;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 150px;
  height: 26px;
  gap: 25px;
  background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='%23C5C6C8FF' stroke-width='2' stroke-dasharray='2%2c 8' stroke-dashoffset='0' stroke-linecap='round'/%3e%3c/svg%3e");
`
const SingleDropZoneActive = styled(SingleDropZone)<{
  isDraggingOver?: boolean
}>`
  color: ${({ theme, isDraggingOver }) =>
    isDraggingOver ? theme.colors.main.primary500 : '#444'};
  background-color: ${({ theme, isDraggingOver }) =>
    isDraggingOver ? theme.colors.main.primary300 : '#fff3ed'};
  background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='%237B7C7DFF' stroke-width='2' stroke-dasharray='2%2c 8' stroke-dashoffset='0' stroke-linecap='round'/%3e%3c/svg%3e");
`

const WrapperDnd = styled.div<{ disabled?: boolean }>`
  margin-left: 50px;
  display: flex;
  flex-direction: row;
  justify-content: space-around;
  width: 80%;

  margin-top: 36px;

  ${({ disabled }) =>
    disabled &&
    css`
      display: none;
      pointer-events: none;
      cursor: default;
    `}
`
const ColumnTitle = styled.div`
  font-size: 14px;
  line-height: 22px;
  margin-bottom: 10px;
  width: 155px;
  text-align: center;
`
const WeekDaysColumn = styled.div``
const PrioriColumn = styled.div``

const DropZoneWrapper = styled.div`
  margin: 6px;
`
const DropContainer = styled.div`
  position: absolute;
`

const PickerWrapper = styled.div`
  display: flex;

  width: 120px;
  margin-top: -20px;
  margin-bottom: -30px;
`
const QuestionContainer = styled.div<{ disabled?: boolean }>`
  display: flex;
  flex-direction: row;

  margin-top: 20px;

  ${({ disabled }) =>
    disabled &&
    css`
      display: none;
      pointer-events: none;
      cursor: default;
    `}

  animation: ${fadeId} 200ms ease-in-out;
`
const QuestionTitle = styled.div`
  font-size: 14px;
  line-height: 30px;
`
const QuestionDescription = styled.div`
  font-size: 10px;
  margin-left: 20px;
  line-height: 20px;
  width: 310px;
`
const DescWrapper = styled.div`
  display: flex;
  flex-direction: column;
`
const IconContainer = styled.div`
  color: ${({ theme }) => theme.colors.main.primary500};
  font-size: 30px;
  display: flex;
  height: 28px;
  justify-content: center;
  align-items: center;
  margin-left: 10px;
  margin-top: 7px;
  cursor: pointer !important;
`
const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 30px;
`
const SelectWrapper = styled.div`
  margin-right: 8px;
  width: 100%;
  height: ${({ theme }) => theme.dimensions.inputHeight};
`
const StyledSelect = styled(SingleSelect)`
  min-width: 121px;
  max-width: 121px;

  div {
    overflow-x: hidden;
  }
`
const LoaderContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`

const Error = styled.div`
  color: red;
`

const ErrorPrereading = styled.div`
  color: red;
  font-size: 10px;
  margin-top: 10px;
  margin-left: 20px;
  max-width: 250px;
`

const DndErrors = styled.div`
  margin-top: 10px;
  padding-left: 10px;
`

const QuestionContent = styled.div`
  width: 250px;
`
